import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { showToast } from "../guest-slices/other.slice"
import { BASEURI, headers, checkTokenErr } from "../../utils/helper"
import axios from "axios"

// ─────────────────────────────────────────────────────────────────────────────
// THUNKS
// ─────────────────────────────────────────────────────────────────────────────

/** GET /specialist/wallet/summary */
export const fetchWalletSummary = createAsyncThunk(
  "wallet/fetchSummary",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASEURI}/specialist/wallet/summary`, {
        headers: headers(),
      })
      if (res.data?.success) return res.data.data
      throw new Error(res.data?.error || "Failed to load wallet")
    } catch (err) {
      checkTokenErr(err, dispatch)
      return rejectWithValue(err?.response?.data?.error || err.message)
    }
  }
)

/** GET /specialist/wallet/transactions?page=&limit=&type= */
export const fetchWalletTransactions = createAsyncThunk(
  "wallet/fetchTransactions",
  async ({ page = 1, limit = 20, type = null } = {}, { dispatch, rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit })
      if (type) params.set("type", type)
      const res = await axios.get(
        `${BASEURI}/specialist/wallet/transactions?${params.toString()}`,
        { headers: headers() }
      )
      if (res.data?.success) return res.data.data
      throw new Error(res.data?.error || "Failed to load transactions")
    } catch (err) {
      checkTokenErr(err, dispatch)
      return rejectWithValue(err?.response?.data?.error || err.message)
    }
  }
)

/** GET /specialist/wallet/payouts?page=&limit= */
export const fetchWalletPayouts = createAsyncThunk(
  "wallet/fetchPayouts",
  async ({ page = 1, limit = 20 } = {}, { dispatch, rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page, limit })
      const res = await axios.get(
        `${BASEURI}/specialist/wallet/payouts?${params.toString()}`,
        { headers: headers() }
      )
      if (res.data?.success) return res.data.data
      throw new Error(res.data?.error || "Failed to load payouts")
    } catch (err) {
      checkTokenErr(err, dispatch)
      return rejectWithValue(err?.response?.data?.error || err.message)
    }
  }
)

/** GET /specialist/connect/status */
export const fetchConnectStatus = createAsyncThunk(
  "wallet/fetchConnectStatus",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASEURI}/specialist/connect/status`, {
        headers: headers(),
      })
      if (res.data?.success) return res.data.data
      throw new Error(res.data?.error || "Failed to fetch connect status")
    } catch (err) {
      checkTokenErr(err, dispatch)
      return rejectWithValue(err?.response?.data?.error || err.message)
    }
  }
)

/** POST /specialist/connect/onboard → returns redirectUrl */
export const startOnboarding = createAsyncThunk(
  "wallet/startOnboarding",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${BASEURI}/specialist/connect/onboard`,
        {},
        { headers: headers() }
      )
      if (res.data?.success) return res.data.data
      throw new Error(res.data?.error || "Failed to start onboarding")
    } catch (err) {
      checkTokenErr(err, dispatch)
      const errorData = err?.response?.data
      const msg = errorData?.message || errorData?.error || err.message || "Failed to start onboarding"
      dispatch(showToast({ type: "error", message: msg }))
      return rejectWithValue(msg)
    }
  }
)


export const getStripeDashboardLink = createAsyncThunk(
  "wallet/getStripeDashboardLink",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${BASEURI}/specialist/connect/dashboard-link`,
        { headers: headers() }
      );
      if (res?.data?.success) {
        return res.data;
      }

      throw new Error(
        res.data?.error || "Failed to get Stripe dashboard link"
      );
    } catch (err) {
      checkTokenErr(err, dispatch);

      const errorData = err?.response?.data;
      const msg =
        errorData?.message ||
        errorData?.error ||
        err.message ||
        "Failed to get Stripe dashboard link";

      dispatch(
        showToast({
          type: "error",
          message: msg,
        })
      );

      return rejectWithValue(msg);
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────────────────────────────────────

const initialState = {
  // Wallet summary
  wallet: null,
  recentTransactions: [],
  summaryLoading: false,
  summaryError: null,

  // Full transactions list
  transactions: [],
  transactionsPagination: null,
  transactionsLoading: false,
  transactionsError: null,

  // Payouts list
  payouts: [],
  payoutsPagination: null,
  payoutsLoading: false,
  payoutsError: null,

  // Connect / onboarding
  connectStatus: null,
  connectLoading: false,
  connectError: null,

  // Onboarding redirect
  onboardingLoading: false,
  onboardingError: null,
}

const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    clearWalletErrors(state) {
      state.summaryError = null
      state.transactionsError = null
      state.payoutsError = null
      state.connectError = null
      state.onboardingError = null
    },
  },
  extraReducers: (builder) => {
    // ── Wallet Summary ──
    builder
      .addCase(fetchWalletSummary.pending, (state) => {
        state.summaryLoading = true
        state.summaryError = null
      })
      .addCase(fetchWalletSummary.fulfilled, (state, { payload }) => {
        state.summaryLoading = false
        state.wallet = payload.wallet
        state.recentTransactions = payload.recentTransactions || []
      })
      .addCase(fetchWalletSummary.rejected, (state, { payload }) => {
        state.summaryLoading = false
        state.summaryError = payload
      })

    // ── Transactions ──
    builder
      .addCase(fetchWalletTransactions.pending, (state) => {
        state.transactionsLoading = true
        state.transactionsError = null
      })
      .addCase(fetchWalletTransactions.fulfilled, (state, { payload }) => {
        state.transactionsLoading = false
        state.transactions = payload.transactions || []
        state.transactionsPagination = payload.pagination || null
      })
      .addCase(fetchWalletTransactions.rejected, (state, { payload }) => {
        state.transactionsLoading = false
        state.transactionsError = payload
      })

    // ── Payouts ──
    builder
      .addCase(fetchWalletPayouts.pending, (state) => {
        state.payoutsLoading = true
        state.payoutsError = null
      })
      .addCase(fetchWalletPayouts.fulfilled, (state, { payload }) => {
        state.payoutsLoading = false
        state.payouts = payload.payouts || []
        state.payoutsPagination = payload.pagination || null
      })
      .addCase(fetchWalletPayouts.rejected, (state, { payload }) => {
        state.payoutsLoading = false
        state.payoutsError = payload
      })

    // ── Connect Status ──
    builder
      .addCase(fetchConnectStatus.pending, (state) => {
        state.connectLoading = true
        state.connectError = null
      })
      .addCase(fetchConnectStatus.fulfilled, (state, { payload }) => {
        state.connectLoading = false
        state.connectStatus = payload
      })
      .addCase(fetchConnectStatus.rejected, (state, { payload }) => {
        state.connectLoading = false
        state.connectError = payload
      })

    // ── Start Onboarding ──
    builder
      .addCase(startOnboarding.pending, (state) => {
        state.onboardingLoading = true
        state.onboardingError = null
      })
      .addCase(startOnboarding.fulfilled, (state) => {
        state.onboardingLoading = false
      })
      .addCase(startOnboarding.rejected, (state, { payload }) => {
        state.onboardingLoading = false
        state.onboardingError = payload
      })

    // Logout reset
    builder.addCase("Logout", () => initialState)
  },
})

export const { clearWalletErrors } = walletSlice.actions
export default walletSlice.reducer
